/**
 * Near-alignment detection for decorative geometry. Two shapes that are *almost* concentric read as
 * a mistake in a way that a deliberate offset never does — the classic failure of composing
 * absolutely-positioned siblings from hand-computed coordinates. Purely geometric: the measured
 * rects give exact positions, so a subtraction catches what a downscaled frame review misses. Ported
 * from the codeo layout measurer.
 */

export interface AlignmentRect {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface AlignmentEntry {
    label: string;
    rect: AlignmentRect;
}

export interface NearAlignedPair {
    /** The contained (inner) element label. */
    element: string;
    /** The containing (outer) element label. */
    element2: string;
    /** Offset of the inner element's center from the outer's, in px (x right, y down). */
    dx: number;
    dy: number;
    rect: AlignmentRect;
}

// Offsets at or under this are treated as exact alignment (sub-pixel layout, transform rounding).
const EXACT_TOL_PX = 2;
// Centers within this fraction of the outer element's smaller dimension count as "meant to be
// centered". Beyond it, the placement reads as deliberate.
const NEAR_FRACTION = 0.1;
// The inner element may poke slightly past the outer's box (strokes, glow) and still count as contained.
const CONTAIN_TOL_PX = 8;

function center(r: AlignmentRect): { cx: number; cy: number } {
    return { cx: r.x + r.width / 2, cy: r.y + r.height / 2 };
}

function contains(outer: AlignmentRect, inner: AlignmentRect): boolean {
    return (
        inner.x >= outer.x - CONTAIN_TOL_PX &&
        inner.y >= outer.y - CONTAIN_TOL_PX &&
        inner.x + inner.width <= outer.x + outer.width + CONTAIN_TOL_PX &&
        inner.y + inner.height <= outer.y + outer.height + CONTAIN_TOL_PX
    );
}

/**
 * Find pairs where one element is contained in another, centered to within NEAR_FRACTION of the
 * outer's size — but not exactly. Those are the "almost aligned" defects worth a finding; exact
 * centering, clearly offset placement, and non-overlapping shapes are left alone.
 */
export function findNearAlignedPairs(entries: AlignmentEntry[]): NearAlignedPair[] {
    const pairs: NearAlignedPair[] = [];
    for (let i = 0; i < entries.length; i++) {
        for (let j = 0; j < entries.length; j++) {
            if (i === j) continue;
            const outer = entries[i];
            const inner = entries[j];
            // Strict size ordering keeps each pair to one orientation.
            if (inner.rect.width * inner.rect.height >= outer.rect.width * outer.rect.height) {
                continue;
            }
            if (!contains(outer.rect, inner.rect)) continue;
            const co = center(outer.rect);
            const ci = center(inner.rect);
            const dx = ci.cx - co.cx;
            const dy = ci.cy - co.cy;
            const tol = Math.min(outer.rect.width, outer.rect.height) * NEAR_FRACTION;
            const dist = Math.max(Math.abs(dx), Math.abs(dy));
            if (dist <= EXACT_TOL_PX || dist > tol) continue;
            pairs.push({
                element: inner.label,
                element2: outer.label,
                dx: Math.round(dx),
                dy: Math.round(dy),
                rect: inner.rect,
            });
        }
    }
    return pairs;
}
